import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import type { AiSetupData } from './SmartQuizSetup';
import './SmartQuiz.css';

const difficulties = ['Easy', 'Medium', 'Hard', 'Mixed'];

const topicTips = [
  { topic: 'Ancient Rome', category: 'History & culture' },
  { topic: 'Solar System', category: 'Astronomy' },
  { topic: 'World Capitals', category: 'Geography' },
  { topic: 'Film Classics', category: 'Pop culture' },
  { topic: 'Human Body', category: 'Biology' },
];

interface Props {
  data: AiSetupData;
  onChange: (d: AiSetupData) => void;
  onGenerate: () => void;
}

const StepAiSetup: React.FC<Props> = ({ data, onChange, onGenerate }) => {
  const update = (partial: Partial<AiSetupData>) => onChange({ ...data, ...partial });

  return (
    <div className="sq-step1">
      <div className="sq-step1__main">
        <div className="sq-step1__card">
          <div className="sq-step1__card-header">
            <FontAwesomeIcon icon={faStar} className="sq-step1__card-icon" />
            <div>
              <h2 className="sq-step1__card-title">Let the AI do the work</h2>
              <p className="sq-step1__card-sub">Smart Quiz generation powered by AI</p>
            </div>
          </div>

          <label className="sq-label">Quiz Topic *</label>
          <textarea
            className="sq-textarea"
            placeholder="Enter a topic (e.g. Ancient Rome, Space, Football...)"
            value={data.topic}
            onChange={(e) => update({ topic: e.target.value })}
            rows={4}
            maxLength={200}
          />

          <div className="sq-step1__slider-row">
            <label className="sq-label">Questions: <span className="sq-step1__count-badge">{data.questionCount}</span></label>
            <input
              type="range"
              min={5}
              max={30}
              value={data.questionCount}
              onChange={(e) => update({ questionCount: Number(e.target.value) })}
              className="sq-step1__slider"
            />
            <div className="sq-step1__slider-labels">
              <span>5</span><span>30</span>
            </div>
          </div>

          <label className="sq-label">Difficulty</label>
          <div className="sq-pills">
            {difficulties.map((d) => (
              <button
                key={d}
                className={`sq-pill ${data.difficulty === d ? 'sq-pill--active' : ''}`}
                onClick={() => update({ difficulty: d })}
              >
                {d}
              </button>
            ))}
          </div>

          <button className="sq-btn sq-btn--primary sq-btn--full" onClick={onGenerate}>
            Generate My Quiz
          </button>
        </div>
      </div>

      {/* Right sidebar — topic tips (desktop only) */}
      <div className="sq-step1__aside">
        <div className="sq-step1__tips">
          <h4 className="sq-step1__tips-title">✨ Topic Tips</h4>
          {topicTips.map((t) => (
            <button
              key={t.topic}
              className={`sq-step1__tip ${data.topic === t.topic ? 'sq-step1__tip--active' : ''}`}
              onClick={() => update({ topic: t.topic })}
            >
              <span className="sq-step1__tip-topic">{t.topic}</span>
              <span className="sq-step1__tip-cat">{t.category}</span>
            </button>
          ))}
        </div>

        <div className="sq-step1__info-card">
          <span className="sq-step1__info-icon">⚡</span>
          <h4 className="sq-step1__info-title">AI-Powered</h4>
          <p className="sq-step1__info-text">
            Our AI generates contextually relevant, fact-checked questions tailored to your topic and difficulty setting.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StepAiSetup;
